/**
 * VacationAccountCard - Detaillierte Urlaubskontoansicht für Mitarbeiter
 * 
 * Zeigt:
 * - Jahresanspruch, Übertrag, Zusatztage
 * - Genommene Tage, verbleibend gesamt
 * - Verbleibender Übertrag, verbleibender laufender Anspruch
 * - Verfallswarnung wenn < 8 Wochen und offene Tage
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, differenceInWeeks, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Palmtree,
  CalendarClock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowRightCircle,
  Clock,
  Gift,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Extracted component to avoid nested component anti-pattern
const StatItem = ({ label, value, subValue, highlight, icon: Icon }) => (
  <div className={`p-3 rounded-lg ${highlight ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/50'}`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-4 h-4 ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
    <p className={`text-xl font-mono font-bold ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
      {typeof value === 'number' ? value.toFixed(1) : value}
      <span className="text-sm font-normal text-muted-foreground"> Tage</span>
    </p>
    {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
  </div>
);

export function VacationAccountCard({ year, userId, compact = false }) {
  const { api, user } = useAuth();
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(!compact);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const targetUserId = userId || user?.id;
        const endpoint = userId && userId !== user?.id 
          ? `/vacation-accounts/${targetUserId}/${year}`
          : `/vacation-accounts/me/${year}`;
        const res = await api.get(endpoint);
        setAccount(res.data);
      } catch (error) {
        console.error('Failed to fetch vacation account:', error);
        // Fallback zu leerem Konto wenn nicht gefunden
        setAccount(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccount();
  }, [api, year, userId, user?.id]);

  if (isLoading) {
    return (
      <div className="card-glass p-6 animate-pulse">
        <div className="h-8 bg-muted/50 rounded w-1/3 mb-4" />
        <div className="h-16 bg-muted/50 rounded" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="card-glass p-6">
        <p className="text-muted-foreground">Keine Kontodaten für {year} verfügbar.</p>
      </div>
    );
  }

  // Berechne Wochen bis zum nächsten Verfall
  const today = new Date();
  const weeksUntilExpiry = account.expiry_next_date
    ? differenceInWeeks(parseISO(account.expiry_next_date), today)
    : null;
  
  const showExpiryWarning = 
    weeksUntilExpiry !== null && 
    weeksUntilExpiry >= 0 && 
    weeksUntilExpiry < 8 && 
    account.expiry_next_days > 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'd. MMMM yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-glass"
      data-testid="vacation-account-card"
    >
      {/* Header */}
      <div 
        className={`p-6 ${compact ? 'cursor-pointer' : ''}`}
        onClick={() => compact && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Palmtree className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Urlaubskonto {year}</h3>
              <p className="text-sm text-muted-foreground">
                {account.remaining_total.toFixed(1)} Tage verbleibend
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-heading font-bold text-emerald-600 dark:text-emerald-400">
                {account.remaining_total.toFixed(1)}
              </p>
              <p className="text-xs text-muted-foreground">verbleibend</p>
            </div>
            {compact && (
              <button className="text-muted-foreground hover:text-foreground">
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Verfallswarnung */}
        {showExpiryWarning && (
          <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-600 dark:text-amber-400 font-medium">Urlaubsverfall droht!</p>
                <p className="text-sm text-amber-700 dark:text-amber-300/80 mt-1">
                  {account.expiry_next_days.toFixed(1)} Tag(e) verfallen am {formatDate(account.expiry_next_date)}, 
                  wenn sie bis dahin nicht genommen werden.
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-200/60 mt-1">
                  Noch {weeksUntilExpiry} Woche(n) Zeit
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detaillierte Ansicht */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4">
          {/* Anspruch-Übersicht */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatItem 
              label="Jahresanspruch" 
              value={account.base_entitlement}
              icon={Calendar}
            />
            <StatItem 
              label="Übertrag Vorjahr" 
              value={account.carryover_from_previous}
              subValue={account.carryover_expiry_date 
                ? `Verfällt: ${formatDate(account.carryover_expiry_date)}`
                : account.carryover_from_previous > 0 ? 'Kein Verfall' : undefined
              }
              highlight={account.remaining_carryover > 0 && account.carryover_expiry_date}
              icon={ArrowRightCircle}
            />
            <StatItem 
              label="Zusatztage" 
              value={account.additional_days}
              icon={Gift}
            />
            <StatItem 
              label="Gesamt verfügbar" 
              value={account.total_available}
              icon={Palmtree}
            />
          </div>

          {/* Verbrauch und Rest */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatItem 
              label="Genommen" 
              value={account.used_days}
              icon={CalendarClock}
            />
            <StatItem 
              label="Ausstehend" 
              value={account.pending_days}
              subValue="Noch nicht genehmigt"
              icon={Clock}
            />
            <StatItem 
              label="Rest Übertrag" 
              value={account.remaining_carryover}
              highlight={account.remaining_carryover > 0 && showExpiryWarning}
              subValue={account.remaining_carryover > 0 ? 'Wird zuerst verbraucht' : undefined}
            />
            <StatItem 
              label="Rest laufendes Jahr" 
              value={account.remaining_current_year}
            />
          </div>

          {/* Verfallene Tage */}
          {account.expired_days > 0 && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-600 dark:text-red-400">
                  {account.expired_days.toFixed(1)} Tag(e) sind bereits verfallen
                </p>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className={`text-xs px-2 py-1 rounded ${
              account.status === 'active' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'bg-muted text-muted-foreground'
            }`}>
              {account.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default VacationAccountCard;
